import { useEffect, useCallback } from 'react';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/core/components/ui/alert-dialog';
import { Plus, Search, X, Loader2, Monitor } from 'lucide-react';
import { useAssetManagementStore } from '../../stores/asset-management-store';
import { AssetGrid } from '../organisms/AssetGrid';
import { AssetUploadDialog } from '../organisms/AssetUploadDialog';
import { AssetDetailDialog } from '../organisms/AssetDetailDialog';
import {
  AssetApplicationType,
  AssetType,
  getApplicationTypeOptions,
  getAssetTypeOptions,
} from '../../types/asset-types';
import { useAuthStore } from '@/core/stores/auth-store';
import { useOperatorStore } from '@/core/stores/operator-store';

export function AssetManagement() {
  const {
    // Data
    assets,
    totalCount,
    currentPage,
    pageSize,
    selectedAsset,
    // Filters
    applicationTypeFilter,
    assetTypeFilter,
    operatorIdFilter,
    machineIdFilter,
    searchQuery,
    // UI States
    isLoading,
    isLoadingDetail,
    isUploading,
    isDeleting,
    isRollingBack,
    error,
    uploadError,
    // Dialog state
    uploadDialogOpen,
    detailDialogOpen,
    deleteDialogOpen,
    assetToDelete,
    // Actions
    fetchAssets,
    uploadAsset,
    deleteAsset,
    rollbackAsset,
    setApplicationTypeFilter,
    setAssetTypeFilter,
    setOperatorIdFilter,
    setMachineIdFilter,
    setSearchQuery,
    setCurrentPage,
    openUploadDialog,
    closeUploadDialog,
    openDetailDialog,
    closeDetailDialog,
    openDeleteDialog,
    closeDeleteDialog,
    clearUploadError,
  } = useAssetManagementStore();

  // Check if user is a distributor with multiple operators
  const isDistributor = useAuthStore((state) => state.isDistributor);
  const operators = useOperatorStore((state) => state.operators);
  const loadOperators = useOperatorStore((state) => state.loadOperators);
  const isLoadingOperators = useOperatorStore((state) => state.isLoading);
  const showOperatorFilter = isDistributor && operators.length > 1;

  const applicationOptions = getApplicationTypeOptions();
  const assetTypeOptions = getAssetTypeOptions();

  // Load operators for distributors on mount
  useEffect(() => {
    if (isDistributor && operators.length === 0 && !isLoadingOperators) {
      loadOperators();
    }
  }, [isDistributor, operators.length, isLoadingOperators, loadOperators]);

  // Fetch assets on mount and filter changes
  useEffect(() => {
    fetchAssets();
  }, [applicationTypeFilter, assetTypeFilter, operatorIdFilter, machineIdFilter, searchQuery, currentPage, fetchAssets]);

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, [setSearchQuery]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, [setSearchQuery]);

  const handleApplicationTypeChange = useCallback((value: string) => {
    setApplicationTypeFilter(value === 'all' ? null : value as AssetApplicationType);
  }, [setApplicationTypeFilter]);

  const handleAssetTypeChange = useCallback((value: string) => {
    setAssetTypeFilter(value === 'all' ? null : value as AssetType);
  }, [setAssetTypeFilter]);

  const handleOperatorChange = useCallback((value: string) => {
    setOperatorIdFilter(value === 'all' ? null : Number(value));
  }, [setOperatorIdFilter]);

  const handleMachineIdChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setMachineIdFilter(null);
    } else {
      // Only allow digits
      const cleanValue = value.replace(/[^0-9]/g, '');
      if (cleanValue) {
        const numValue = parseInt(cleanValue, 10);
        if (!isNaN(numValue) && numValue > 0) {
          setMachineIdFilter(numValue);
        }
      }
    }
  }, [setMachineIdFilter]);

  const handleClearMachineId = useCallback(() => {
    setMachineIdFilter(null);
  }, [setMachineIdFilter]);

  const handleViewAsset = useCallback((asset: { assetId: number }) => {
    openDetailDialog(asset.assetId);
  }, [openDetailDialog]);

  const handleDeleteConfirm = useCallback(async () => {
    if (assetToDelete) {
      await deleteAsset(assetToDelete.assetId);
    }
  }, [assetToDelete, deleteAsset]);

  const handleUploadNewVersion = useCallback(() => {
    closeDetailDialog();
    openUploadDialog();
  }, [closeDetailDialog, openUploadDialog]);

  const handleUpload = useCallback(async (request: Parameters<typeof uploadAsset>[0]) => {
    await uploadAsset(request);
  }, [uploadAsset]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Asset Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage icons, images, videos, and Lottie animations for your applications.
          </p>
        </div>
        <Button onClick={openUploadDialog}>
          <Plus className="h-4 w-4 mr-2" />
          Upload Asset
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Operator Filter (only for distributors with multiple operators) */}
        {showOperatorFilter && (
          <Select
            value={operatorIdFilter?.toString() || 'all'}
            onValueChange={handleOperatorChange}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Operators" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Operators</SelectItem>
              {operators.map((op) => (
                <SelectItem key={op.operatorId} value={op.operatorId.toString()}>
                  {op.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Search */}
        <div className="relative flex-1 min-w-[250px] max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search by Name or Description"
            value={searchQuery}
            onChange={handleSearch}
            className="pl-10 pr-10"
          />
          {searchQuery && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Application Type Filter */}
        <Select
          value={applicationTypeFilter || 'all'}
          onValueChange={handleApplicationTypeChange}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Applications" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Applications</SelectItem>
            {applicationOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Asset Type Filter */}
        <Select
          value={assetTypeFilter || 'all'}
          onValueChange={handleAssetTypeChange}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {assetTypeOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Machine ID Filter */}
        <div className="relative w-[150px]">
          <Monitor className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="Machine ID"
            value={machineIdFilter ?? ''}
            onChange={handleMachineIdChange}
            className="pl-10 pr-10"
          />
          {machineIdFilter && (
            <button
              onClick={handleClearMachineId}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Grid */}
      <AssetGrid
        assets={assets}
        isLoading={isLoading}
        error={error}
        totalCount={totalCount}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onView={handleViewAsset}
        onDelete={openDeleteDialog}
        onRetry={fetchAssets}
      />

      {/* Upload Dialog */}
      <AssetUploadDialog
        open={uploadDialogOpen}
        onClose={closeUploadDialog}
        onUpload={handleUpload}
        isUploading={isUploading}
        error={uploadError}
        onClearError={clearUploadError}
        initialOperatorId={operatorIdFilter}
        initialMachineId={machineIdFilter}
        initialApplicationType={applicationTypeFilter}
      />

      {/* Detail Dialog */}
      <AssetDetailDialog
        open={detailDialogOpen}
        onClose={closeDetailDialog}
        assetData={selectedAsset}
        isLoading={isLoadingDetail}
        isRollingBack={isRollingBack}
        isDeleting={isDeleting}
        onRollback={rollbackAsset}
        onDelete={deleteAsset}
        onUploadNewVersion={handleUploadNewVersion}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={closeDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Asset?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &quot;{assetToDelete?.name}&quot; and all its versions.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
